import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function Stats() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [ledGroups, setLedGroups] = useState([]);
  const [scope, setScope] = useState("all"); // 'all' | group_id

  useEffect(() => {
    api.get("/groups").then((r) => setLedGroups(r.data.filter((g) => g.is_leader))).catch((e) => console.error("stats: load groups failed", e));
  }, []);

  useEffect(() => {
    setData(null);
    const params = scope === "all" ? {} : { params: { group_id: scope } };
    api.get("/analytics/overview", params)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Unable to load"));
  }, [scope]);

  if (user && !user.is_leader) return <Navigate to="/app" replace />;
  if (err) return <div className="text-muted-foreground">{err}</div>;

  const cards = data ? [
    ["Total users", data.total_users],
    ["Active (any progress)", data.active_users],
    ["Finished all 11 weeks", data.finished_all_11],
    ["Covenant signed", data.covenant_signed],
    ["Participants", data.participants],
    ["Leaders", data.leaders],
    ["Groups", data.total_groups],
    ["Journal entries", data.total_journal_entries],
  ] : [];
  const maxWeek = data ? Math.max(1, ...data.by_week.map((w) => w.completions)) : 1;

  return (
    <div className="max-w-5xl mx-auto">
      <p className="text-xs uppercase tracking-[0.2em] text-primary">Leader dashboard</p>
      <h1 className="font-serif text-5xl mt-3 leading-tight">The people, the progress.</h1>
      <p className="text-muted-foreground mt-3 max-w-xl">
        Watch the eleven weeks unfold across your community. Numbers won't disciple anyone —
        but they'll tell you where to pay attention.
      </p>

      {ledGroups.length >= 2 && (
        <div className="mt-10 flex flex-wrap items-center gap-2 text-xs" data-testid="stats-group-switcher">
          <span className="uppercase tracking-widest text-muted-foreground mr-2">Scope</span>
          <button data-testid="stats-scope-all" onClick={() => setScope("all")}
            className={`px-3 py-1.5 border ${scope === "all" ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground"}`}>
            All groups
          </button>
          {ledGroups.map((g) => (
            <button key={g.id} data-testid={`stats-scope-${g.id}`}
              onClick={() => setScope(g.id)}
              className={`px-3 py-1.5 border ${scope === g.id ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground"}`}>
              {g.name}
            </button>
          ))}
        </div>
      )}

      {!data ? (
        <div className="mt-10 text-muted-foreground">Loading…</div>
      ) : (
        <>
          <section className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {cards.map(([label, value]) => (
              <div key={label} data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}
                className="border border-border bg-card p-6">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
                <div className="font-serif text-4xl mt-3 tabular-nums">{value}</div>
              </div>
            ))}
          </section>

          <section className="mt-16">
            <h2 className="font-serif text-2xl mb-6 flex items-center gap-4">
              <span>Completions by week</span>
              <span className="flex-1 rule-line mb-1"></span>
            </h2>
            <ul className="space-y-3">
              {data.by_week.map((w) => (
                <li key={w.week_number} className="flex items-center gap-4" data-testid={`by-week-${w.week_number}`}>
                  <span className="text-xs w-14 uppercase tracking-widest text-muted-foreground tabular-nums">Week {w.week_number}</span>
                  <div className="flex-1 h-1 bg-secondary relative">
                    <div className="absolute inset-y-0 left-0 bg-primary" style={{ width: `${(w.completions / maxWeek) * 100}%` }} />
                  </div>
                  <span className="text-sm w-10 text-right tabular-nums">{w.completions}</span>
                </li>
              ))}
            </ul>
          </section>
        </>
      )}
    </div>
  );
}
